import { Box, Column, Columns, IconTick, Bleed } from 'braid-design-system';
import {
  type ComponentProps,
  useRef,
  type FC,
  type MouseEventHandler,
  type FormEventHandler,
} from 'react';

import { ResponsiveText } from 'src/App/AppPageBlock/ResponsiveText';

import * as styles from './styles.css';

interface XSmallCheckboxProps {
  id: string;
  checked: boolean;
  label: string;
  onChange: FormEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLInputElement>;
  ariaLabel?: string;
}

export const XSmallCheckbox: FC<XSmallCheckboxProps> = ({
  id,
  label,
  checked,
  ariaLabel,
  onChange,
  onClick,
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  return (
    <Columns space="xsmall" align="left">
      <Column width="content">
        <Bleed vertical="xxsmall">
          <Box display="flex" position="relative">
            <Box
              component="input"
              type="checkbox"
              id={id}
              position="absolute"
              zIndex={1}
              value={id}
              onChange={onChange}
              onClick={onClick}
              checked={checked}
              className={styles.checkbox}
              cursor="pointer"
              opacity={0}
              ref={checkboxRef}
              aria-checked={checked}
              aria-required={false}
              tabIndex={0}
              role="checkbox"
              aria-label={ariaLabel ?? label}
            />
            <Box
              flexShrink={0}
              position="relative"
              className={styles.styledCheckboxParent}
              background="surface"
              borderRadius="standard"
            >
              <FieldOverlay variant="default" visible />

              <FieldOverlay
                background={checked ? 'formAccent' : undefined}
                className={styles.checkboxSelected}
              >
                <Indicator checked={checked} />
              </FieldOverlay>

              <FieldOverlay
                variant="focus"
                className={styles.checkboxFocusOverlay}
              />

              <FieldOverlay
                variant="formAccent"
                className={styles.checkboxHoverOverlay}
              >
                <Indicator checked={true} />
              </FieldOverlay>
              <Box />
            </Box>
          </Box>
        </Bleed>
      </Column>
      <Column>
        <Box
          component="label"
          role="label"
          htmlFor={id}
          aria-hidden
          cursor="pointer"
          onClick={onClick}
        >
          <ResponsiveText
            size={{
              mobile: 'standard',
              tabletAndDesktop: 'small',
            }}
          >
            {label}
          </ResponsiveText>
        </Box>
      </Column>
    </Columns>
  );
};

// The following FieldOverlay component is a simplified and combined version of the component of the same name and the Overlay component in Braid
type FieldOverlayVariant = 'default' | 'focus' | 'formAccent';
interface FieldOverlayProps
  extends Partial<
    Pick<
      ComponentProps<typeof Box>,
      'children' | 'zIndex' | 'background' | 'boxShadow' | 'className'
    >
  > {
  visible?: boolean;
  variant?: FieldOverlayVariant;
}
const boxShadowForVariant: Record<
  FieldOverlayVariant,
  FieldOverlayProps['boxShadow']
> = {
  default: 'borderField',
  focus: 'outlineFocus',
  formAccent: 'borderFormAccent',
};

const FieldOverlay = ({
  zIndex,
  background,
  visible = false,
  className,
  variant,
  children,
}: FieldOverlayProps) => (
  <Box
    component="span"
    position="absolute"
    zIndex={zIndex}
    pointerEvents="none"
    background={background}
    borderRadius="standard"
    boxShadow={boxShadowForVariant[variant!]}
    transition="fast"
    inset={0}
    opacity={!visible ? 0 : undefined}
    className={[className]}
  >
    {children}
  </Box>
);

// Simplified and copied from Braid
interface IndicatorProps {
  checked: boolean;
}
const Indicator: FC<IndicatorProps> = ({ checked }) => (
  <Box
    height="full" // Needed for IE11
    transition="fast"
    position="relative"
    className={styles.checkboxIndicator}
  >
    <Box
      position="absolute"
      inset={0}
      transition="fast"
      opacity={!checked ? 0 : undefined}
    >
      <IconTick size="fill" tone="neutral" />
    </Box>
  </Box>
);
